// Rules for VALIDATION_CONSTRAINTS.md section 8 — boundary event attachment and content.
// Boundary attachment is wired via node.parentNode (not via sequence flow edges).
// Cardinality (8.1) is covered by rule 3.6 and intentionally skipped here.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import {
  buildLookup,
  getNode,
  getOutgoing,
  isActivity,
  isBoundaryEvent,
  labelOf,
} from '../helpers';

// 8.1: Skipped — boundary-event cardinality (0 in / 1 out) is enforced by rule 3.6.

// 8.2: Every boundary node must have a parentNode set, and the parent must exist in the same process.
const rule_8_2_BoundaryParentExists: ValidationRule = {
  id: '8.2',
  description: 'BoundaryEvent must have a parentNode that exists in the same process',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isBoundaryEvent(n)) continue;
      const parentId = n.parentNode;
      if (!parentId) {
        issues.push({
          severity: 'error',
          rule: '8.2',
          message: `${labelOf(n)} has no parentNode — boundary events must attach to an activity.`,
          nodeIds: [n.id],
        });
        continue;
      }
      if (!getNode(lookup, parentId)) {
        issues.push({
          severity: 'error',
          rule: '8.2',
          message: `${labelOf(n)} references missing parent "${parentId}" (not found in process "${process.name}").`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_8_2_BoundaryParentExists);

// 8.3: The parentNode host must be an Activity subclass (covers 8.4 — host/boundary linkage consistency).
const rule_8_3_BoundaryHostIsActivity: ValidationRule = {
  id: '8.3',
  description: 'BoundaryEvent host (parentNode) must be an Activity',
  // NOTE: Covers both constraint 8.3 (host must be Activity) and 8.4
  // (bidirectional activity/boundary linkage consistency) — with parentNode
  // wiring the two checks collapse into a single host-is-Activity assertion.
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isBoundaryEvent(n)) continue;
      const parentId = n.parentNode;
      if (!parentId) continue; // already reported by 8.2
      const parent = getNode(lookup, parentId);
      if (!parent) continue; // already reported by 8.2
      if (!isActivity(parent)) {
        issues.push({
          severity: 'error',
          rule: '8.3',
          message: `${labelOf(n)} is attached to non-activity host ${labelOf(parent)}; boundary events must attach to an Activity.`,
          nodeIds: [n.id, parent.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_8_3_BoundaryHostIsActivity);

// 8.4: Merged into 8.3 above. (Constraint 8.4 — bidirectional host/boundary
// linkage consistency — is automatically satisfied by the parentNode model
// because there is only one direction of reference to validate.)

// 8.5: ErrorBoundaryEvent — warn if BOTH exceptionType and errorMessage empty.
//      Empty exceptionType IS valid (catch-all) per UI doc; warn because such
//      a catch-all without an errorMessage is hard to identify at runtime.
const rule_8_5_ErrorBoundaryIdentification: ValidationRule = {
  id: '8.5',
  description: 'ErrorBoundaryEvent catch-all without errorMessage is hard to identify',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'ErrorBoundaryEvent') continue;
      const exceptionType = ((n.data as any)?.exceptionType ?? '').toString().trim();
      const errorMessage = (n.data?.errorMessage ?? '').toString().trim();
      if (!exceptionType && !errorMessage) {
        issues.push({
          severity: 'warning',
          rule: '8.5',
          message: `${labelOf(n)}: Catch-all error boundary has no errorMessage for identification.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_8_5_ErrorBoundaryIdentification);

// 8.6: TimerBoundaryEvent requires a timerDefinition or timerDate.
const rule_8_6_TimerBoundary: ValidationRule = {
  id: '8.6',
  description: 'TimerBoundaryEvent requires a timerDefinition or timerDate',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'TimerBoundaryEvent') continue;
      const def = (n.data?.timerDefinition ?? '').toString().trim();
      const date = (n.data?.timerDate ?? '').toString().trim();
      if (!def && !date) {
        issues.push({
          severity: 'error',
          rule: '8.6',
          message: `${labelOf(n)} requires a timerDefinition or timerDate.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_8_6_TimerBoundary);

// 8.7: MessageBoundaryEvent / SignalBoundaryEvent require their name field.
const rule_8_7_MessageSignalBoundary: ValidationRule = {
  id: '8.7',
  description: 'MessageBoundaryEvent/SignalBoundaryEvent require message/signal name',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type === 'MessageBoundaryEvent') {
        const name = (n.data?.messageName ?? '').toString().trim();
        if (!name) {
          issues.push({
            severity: 'error',
            rule: '8.7',
            message: `${labelOf(n)} requires a non-empty messageName.`,
            nodeIds: [n.id],
          });
        }
      } else if (n.type === 'SignalBoundaryEvent') {
        const name = (n.data?.signalName ?? '').toString().trim();
        if (!name) {
          issues.push({
            severity: 'error',
            rule: '8.7',
            message: `${labelOf(n)} requires a non-empty signalName.`,
            nodeIds: [n.id],
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_8_7_MessageSignalBoundary);

// 8.8: Error/Timer boundary events are always interrupting. Export layer self-heals,
//      but reject externally-edited JSON where cancelActivity is explicitly false.
const rule_8_8_InterruptingBoundary: ValidationRule = {
  id: '8.8',
  description: 'Error/Timer boundary events must be interrupting (cancelActivity=true)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'ErrorBoundaryEvent' && n.type !== 'TimerBoundaryEvent') continue;
      if (n.data?.cancelActivity === false) {
        issues.push({
          severity: 'error',
          rule: '8.8',
          message: `${labelOf(n)}: cancelActivity must be true for Error/Timer boundary events.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_8_8_InterruptingBoundary);

// 8.9: At most one catch-all (empty exceptionType) ErrorBoundaryEvent per host activity.
const rule_8_9_SingleCatchAllErrorBoundary: ValidationRule = {
  id: '8.9',
  description: 'An activity can have at most one catch-all ErrorBoundaryEvent',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    const catchAllsByHost = new Map<string, typeof process.nodes>();
    for (const n of process.nodes) {
      if (n.type !== 'ErrorBoundaryEvent') continue;
      const parentId = n.parentNode;
      if (!parentId) continue;
      const exceptionType = ((n.data as any)?.exceptionType ?? '').toString().trim();
      if (exceptionType) continue;
      const list = catchAllsByHost.get(parentId) ?? [];
      list.push(n);
      catchAllsByHost.set(parentId, list);
    }
    for (const [hostId, boundaries] of catchAllsByHost) {
      if (boundaries.length > 1) {
        issues.push({
          severity: 'error',
          rule: '8.9',
          message: `Activity "${hostId}" has ${boundaries.length} catch-all ErrorBoundaryEvents; only one is allowed.`,
          nodeIds: [hostId, ...boundaries.map(b => b.id)],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_8_9_SingleCatchAllErrorBoundary);

// 8.10: Warning — boundary outgoing target equals host activity (infinite-loop risk).
const rule_8_10_BoundaryTargetsHost: ValidationRule = {
  id: '8.10',
  description: 'Boundary outgoing target equals host activity (infinite-loop risk)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isBoundaryEvent(n)) continue;
      const parentId = n.parentNode;
      if (!parentId) continue;
      const outgoing = getOutgoing(lookup, n.id);
      for (const edge of outgoing) {
        if (edge.target === parentId) {
          issues.push({
            severity: 'warning',
            rule: '8.10',
            message: `${labelOf(n)} outgoing flow targets its host activity — potential infinite loop unless modeled as a RecursiveConnection.`,
            nodeIds: [n.id, parentId],
            edgeIds: [edge.id],
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_8_10_BoundaryTargetsHost);

// 8.11: Warning — specific + catch-all error boundary on same activity (ambiguity).
const rule_8_11_SpecificAndCatchAllAmbiguity: ValidationRule = {
  id: '8.11',
  description: 'Specific and catch-all error boundaries on the same activity depend on list order',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    type Bucket = { specific: string[]; catchAll: string[] };
    const byHost = new Map<string, Bucket>();
    for (const n of process.nodes) {
      if (n.type !== 'ErrorBoundaryEvent') continue;
      const parentId = n.parentNode;
      if (!parentId) continue;
      const exceptionType = ((n.data as any)?.exceptionType ?? '').toString().trim();
      const bucket = byHost.get(parentId) ?? { specific: [], catchAll: [] };
      if (exceptionType) bucket.specific.push(n.id);
      else bucket.catchAll.push(n.id);
      byHost.set(parentId, bucket);
    }
    for (const [hostId, bucket] of byHost) {
      if (bucket.specific.length > 0 && bucket.catchAll.length > 0) {
        issues.push({
          severity: 'warning',
          rule: '8.11',
          message: `Activity "${hostId}" has both specific and catch-all ErrorBoundaryEvents; matching depends on list order.`,
          nodeIds: [hostId, ...bucket.specific, ...bucket.catchAll],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_8_11_SpecificAndCatchAllAmbiguity);
